/**
 * Mini-modale ouverte au clic sur le badge statut d'un planning d'inventaire.
 * Affiche le responsable + op1 + op2 avec leur numéro WhatsApp et un message
 * pré-rempli adapté au statut (à faire / en cours / en retard / terminé).
 *
 * Pas d'appel backend : génération directe des liens `wa.me` côté client à
 * partir de `agents` (qui contient `whatsapp_phone`).
 *
 * Props :
 * - planning : la ligne planning cliquée (zone_name, date, statut, responsable_id/name, op1_id/name, op2_id/name)
 * - agents   : liste des agents avec whatsapp_phone
 * - onClose()
 */
import React, { useMemo, useState } from 'react';
import { X, Send, MessageCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';

const SIGNATURE = '\n\nAdmin - Pharmacie SADA';

const STATUT_TEMPLATES = {
  a_faire: (zone, dateFr) =>
    `Bonjour,\n\nRappel : inventaire « ${zone} » prévu le ${dateFr}.\nMerci de vous tenir prêt(e). Bonne journée.${SIGNATURE}`,
  en_cours: (zone, dateFr) =>
    `Bonjour,\n\nL'inventaire « ${zone} » (${dateFr}) est en cours.\nMerci de finaliser la saisie et de clôturer dès que possible.${SIGNATURE}`,
  en_retard: (zone, dateFr) =>
    `Bonjour,\n\nL'inventaire « ${zone} » du ${dateFr} est en retard.\nPouvez-vous le démarrer dès que possible ou m'indiquer le motif ? Merci.${SIGNATURE}`,
  termine: (zone, dateFr) =>
    `Bonjour,\n\nMerci pour la clôture de l'inventaire « ${zone} » du ${dateFr}.\nBon travail.${SIGNATURE}`,
};

const STATUT_LABEL = {
  a_faire: 'À faire',
  en_cours: 'En cours',
  en_retard: 'En retard',
  termine: 'Terminé',
};

// Normalisation téléphone alignée sur le backend `_build_whatsapp_url` :
// on conserve TOUS les chiffres et on préfixe simplement « 225 » si le
// numéro est en format national 10 chiffres (ex: 0770000000 → 2250770000000).
// ⚠️ NE PAS retirer le 0 initial — WhatsApp iOS exige le 0 dans le national.
const formatPhone = (raw) => {
  if (!raw) return null;
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00225')) digits = digits.slice(2);
  if (!digits.startsWith('225') && digits.length === 10) digits = '225' + digits;
  if (digits.length < 12) return null;
  return digits;
};

// En Côte d'Ivoire, le téléphone est dans 99% des cas le numéro WhatsApp.
// On utilise `whatsapp_phone` si renseigné, sinon fallback sur `phone`.
const agentWhatsApp = (agent) => formatPhone(agent?.whatsapp_phone || agent?.phone);

const PlanningWhatsAppDialog = ({ planning, agents, onClose }) => {
  const [message, setMessage] = useState(() => {
    const tpl = STATUT_TEMPLATES[planning.statut] || STATUT_TEMPLATES.a_faire;
    return tpl(planning.zone_name, planning.date);
  });

  // Map id → agent (avec whatsapp_phone)
  const agentMap = useMemo(() => {
    const m = {};
    (agents || []).forEach(a => { m[a.id] = a; });
    return m;
  }, [agents]);

  const recipients = [
    planning.responsable_id && {
      role: 'Responsable',
      id: planning.responsable_id,
      name: planning.responsable_name,
      agent: agentMap[planning.responsable_id],
    },
    planning.op1_id && {
      role: 'Op1 (compteur)',
      id: planning.op1_id,
      name: planning.op1_name,
      agent: agentMap[planning.op1_id],
    },
    planning.op2_id && {
      role: 'Op2 (recompteur)',
      id: planning.op2_id,
      name: planning.op2_name,
      agent: agentMap[planning.op2_id],
    },
  ].filter(Boolean);

  const sendTo = (recipient) => {
    const wa = agentWhatsApp(recipient.agent);
    if (!wa) return;
    const personalised = `Bonjour ${recipient.name.split(' ')[0]},\n\n` + message.replace(/^Bonjour[^\n]*\n\n?/, '');
    const url = `https://wa.me/${wa}?text=${encodeURIComponent(personalised)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
      data-testid="planning-wa-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-emerald-600 to-emerald-700 px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            <div>
              <h2 className="font-semibold">Rappel WhatsApp</h2>
              <p className="text-xs text-white/80">
                {planning.zone_name} · {planning.date} · {STATUT_LABEL[planning.statut] || planning.statut}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="planning-wa-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1 block">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="w-full border border-zinc-300 rounded-md p-2 text-sm font-sans"
              data-testid="planning-wa-message"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Le prénom de chaque destinataire sera ajouté automatiquement en début de message.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-zinc-700 mb-2">Destinataires</p>
            <div className="space-y-2">
              {recipients.map((r) => {
                const wa = agentWhatsApp(r.agent);
                return (
                  <div
                    key={r.id}
                    className={`flex items-center justify-between rounded-md border p-2 ${
                      wa ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
                    }`}
                    data-testid={`planning-wa-recipient-${r.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-900 truncate">{r.name}</div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                        <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[10px] font-mono">{r.role}</span>
                        {wa
                          ? <span className="font-mono">+{wa}</span>
                          : <span className="text-amber-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Pas de WhatsApp</span>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => sendTo(r)}
                      disabled={!wa}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                      data-testid={`planning-wa-send-${r.id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" />
                      Envoyer
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-2.5 text-xs text-blue-900">
            💡 Chaque clic ouvre WhatsApp Web/Mobile avec le message pré-rempli.
            Vous validez l'envoi sur votre app WhatsApp (gratuit, pas d'API).
          </div>
        </div>

        <div className="shrink-0 border-t border-zinc-200 bg-white px-5 py-3 flex justify-end">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </div>
      </div>
    </div>
  );
};

export default PlanningWhatsAppDialog;
